import { cloneElement, isValidElement, useCallback, useEffect, useId, useRef, useState, type ReactElement, type ReactNode, type Ref } from 'react';
import { cn } from '../../utils/cn';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import type { Placement } from '../../hooks/useFloating';
import { FloatingPanel } from '../shared/FloatingPanel';
import { isFocusVisible, trackFocusModality } from '../../utils/focusVisible';
import { mergeRefs } from './mergeRefs';

export interface TooltipProps {
  content: ReactNode;
  /** Single focusable/hoverable element. */
  children: ReactElement;
  placement?: Placement;
  /** Open delay in ms on hover. Keyboard focus always opens immediately. */
  delay?: number;
  disabled?: boolean;
  className?: string;
  /** Keyboard shortcut hint rendered after the content. */
  shortcut?: ReactNode;
}

/** Design Direction v2: tooltips wait 450 ms before appearing. */
export const TOOLTIP_DELAY = 450;

/**
 * Shared "group" window. Once any tooltip has been shown, moving to an adjacent trigger within this
 * window opens the next tooltip instantly instead of re-serving the full delay — the behaviour that
 * makes a toolbar feel responsive rather than sticky.
 */
export const TOOLTIP_GROUP_MS = 300;

let lastDismissedAt = 0;

/** Test seam: forgets the shared group window. */
export function resetTooltipGroup(): void {
  lastDismissedAt = 0;
}

function inGroupWindow(): boolean {
  return lastDismissedAt > 0 && Date.now() - lastDismissedAt <= TOOLTIP_GROUP_MS;
}

type ChildProps = {
  ref?: Ref<HTMLElement>;
  onMouseEnter?: (e: unknown) => void;
  onMouseLeave?: (e: unknown) => void;
  onFocus?: (e: unknown) => void;
  onBlur?: (e: unknown) => void;
  'aria-describedby'?: string;
};

/**
 * Hover/focus tooltip. Non-modal, never traps focus, closes on Escape, blur and pointer-leave.
 * Positioning, the arrow and the enter/exit animation come from the one floating primitive.
 *
 * Focus opens it only when the focus is *visible* — the keyboard case. Clicking a button focuses it too,
 * and a tooltip that popped up under the pointer on every click would cover the thing just clicked; the
 * pointer user already has the hover tooltip. `utils/focusVisible` supplies the same heuristic the browser
 * applies to `:focus-visible`.
 */
export function Tooltip({ content, children, placement = 'top', delay = TOOLTIP_DELAY, disabled, className, shortcut }: TooltipProps) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLElement | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openRef = useRef(false);
  openRef.current = open;

  const clear = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  const show = useCallback(
    (immediate = false) => {
      if (disabled) return;
      clear();
      if (immediate || delay <= 0 || inGroupWindow()) setOpen(true);
      else timer.current = setTimeout(() => setOpen(true), delay);
    },
    [delay, disabled],
  );
  const hide = useCallback(() => {
    clear();
    if (openRef.current) lastDismissedAt = Date.now();
    setOpen(false);
  }, []);
  useEffect(() => {
    trackFocusModality();
    return clear;
  }, []);
  useEscapeKey(hide, open);

  if (!isValidElement<ChildProps>(children)) return children;
  const childProps = children.props;
  const trigger = cloneElement(children, {
    ref: mergeRefs<HTMLElement>(anchorRef, childProps.ref),
    'aria-describedby': open ? id : childProps['aria-describedby'],
    onMouseEnter: (e: unknown) => {
      childProps.onMouseEnter?.(e);
      show();
    },
    onMouseLeave: (e: unknown) => {
      childProps.onMouseLeave?.(e);
      hide();
    },
    onFocus: (e: unknown) => {
      childProps.onFocus?.(e);
      // Pointer focus (a click on the trigger) must not open it; hover already did, or will.
      if (isFocusVisible()) show(true);
    },
    onBlur: (e: unknown) => {
      childProps.onBlur?.(e);
      hide();
    },
  });

  return (
    <>
      {trigger}
      <FloatingPanel
        anchorRef={anchorRef}
        open={open && !disabled}
        placement={placement}
        offset={6}
        arrow
        elevation={3}
        surface={3}
        radius="md"
        closeOnOutsideClick={false}
        closeOnEscape={false}
        zIndex="var(--zo-z-toast)"
        id={id}
        role="tooltip"
        // The panel and its arrow are the inverse surface; `style` beats FloatingPanel's surface class,
        // and the arbitrary variant recolours the arrow the panel renders for us.
        style={{ background: 'var(--zo-surface-inverse)' }}
        className={cn(
          'zo-tooltip pointer-events-none max-w-72 px-2 py-1 type-caption text-fg-inverse',
          '[&_[data-zo-arrow]]:bg-surface-inverse [&_[data-zo-arrow]]:shadow-none',
          shortcut != null && 'flex items-center gap-2',
          className,
        )}
      >
        <span>{content}</span>
        {shortcut != null ? <span className="text-fg-inverse/70">{shortcut}</span> : null}
      </FloatingPanel>
    </>
  );
}
